import React, { CSSProperties, useEffect, useState } from "react";

import TimeDiff from "components/basic/timer/TimeDiff";
import { ProgressSpinner } from "primereact/progressspinner";
import useCopyDataset from "hooks/useCopyDataset";
import { Dropdown } from "primereact/dropdown";
import useTimerThreshold from "hooks/useTimerThreshold";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { formatTime } from "utils/time-utils";
import { ISparkLineHandlerProps, ValueLabel } from "./SparkLineTypes";
import SparkLineDisconnectedLabel from "./SparkLineDisconnectedLabel";
import "./SparkLine.scss";

type SparkLineLabelProps = ISparkLineHandlerProps & {
  valueLabel: ValueLabel;
  isExpanded?: boolean;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
  mappedValue?: string | number;
  allCharts?: Record<number, string> | undefined;
};

const SparkLineLabel: React.FC<SparkLineLabelProps> = ({
  valueLabel,
  isExpanded,
  setValueLabel,
  mappedValue,
  allCharts,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { isExpired, resetTimer } = useTimerThreshold(props.maxAge, props.lastValueTime?.getTime());
  useCopyDataset(isHovered, props.title, props.dataset);

  useEffect(() => {
    resetTimer(props.lastValueTime?.getTime());
  }, [props.lastValueTime, resetTimer]);

  useEffect(() => {
    if (isExpired) {
      setValueLabel(prev => ({
        ...prev,
        content: <SparkLineDisconnectedLabel isMultipleCharts={props.isMultipleCharts} />,
        textColor: "red",
      }));
    }
  }, [isExpired, setValueLabel, props.isMultipleCharts]);

  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;
  const minValue = Math.min(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const maxValue = Math.max(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const style = {
    fontSize: props.labelFontSize || "2.5rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    lineHeight: "2.5rem",
    paddingLeft: props.isMultipleCharts ? "" : "0.5rem",
    top: props.vertical === "top" ? "0" : "auto",
    right: props.horizontal === "right" ? "0" : "auto",
    background: valueLabel.bgColor,
    textWrap: "wrap",
    overflowWrap: "break-word",
    color: isExpired ? "red" : valueLabel.textColor,
    textAlign: "left",
  } as CSSProperties;

  const chartOptions = allCharts
    ? Object.entries(allCharts).map(([indexStr, title]) => ({
        label: title,
        value: Number(indexStr),
      }))
    : [];

  return (
    <>
      {props.isMultipleCharts &&
        props.generalProperties?.chartsInOneBox &&
        allCharts &&
        props.generalProperties.displayTitlesInRow && (
          <div className="flex flex-row-reverse align-items-center justify-content-between">
            <div className="flex align-items-center justify-content-end gap-1 w-6">
              {props.isLoading && (
                <div className="flex justify-content-end">
                  <ProgressSpinner className="label-progress-spinner" />
                </div>
              )}
              {props.generalProperties?.hideValues ? null : (
                <div
                  id={`${id}`}
                  className="sparkline-value"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {!isExpired && mappedValue ? mappedValue : valueLabel.content}
                </div>
              )}
            </div>
            <div className="sparkline-title w-full">
              <div className="flex gap-1 flex-wrap">
                {Object.entries(allCharts).map(([indexStr, title]) => {
                  const index = Number(indexStr);
                  const isActive = props.chartIndex === index;

                  return (
                    <div
                      key={index}
                      onClick={() => props.advancedProps?.setExpandedChartIndex?.(index)}
                      className={`inline-multiple-charts-title${isActive ? "-active" : ""}`}
                    >
                      {title}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      {props.isMultipleCharts &&
        props.generalProperties?.chartsInOneBox &&
        allCharts &&
        !props.generalProperties.displayTitlesInRow && (
          <div className="flex flex-row-reverse align-items-center justify-content-between w-full gap-2">
            <div className="flex align-items-center justify-content-end gap-2 w-6">
              {props.isLoading && (
                <div className="flex justify-content-end">
                  <ProgressSpinner className="label-progress-spinner" />
                </div>
              )}
              {props.generalProperties?.hideValues ? null : (
                <div
                  id={id}
                  className="sparkline-value"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {!isExpired && mappedValue ? mappedValue : valueLabel.content}
                </div>
              )}
            </div>
            <div className="w-4">
              <Dropdown
                value={props.chartIndex}
                options={chartOptions}
                onChange={e => props.advancedProps?.setExpandedChartIndex?.(e.value)}
                className="w-full h-2rem flex align-items-center font-bold"
                placeholder="Select Chart"
                panelClassName="centered-dropdown-panel"
                style={{
                  background: "#f3f5f6",
                }}
                dropdownIcon="pi pi-chevron-down"
              />
            </div>
          </div>
        )}

      {props.isMultipleCharts && !props.generalProperties?.chartsInOneBox && (
        <div className="flex flex-row-reverse align-items-center justify-content-between">
          <div className="flex align-items-center justify-content-end gap-2 w-12">
            {props.generalProperties?.hideValues ? null : (
              <div
                id={`${id}`}
                className="sparkline-value"
                style={!props.isMultipleCharts ? style : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {!isExpired && mappedValue ? mappedValue : valueLabel.content}
              </div>
            )}
            {!isExpanded && !props.generalProperties?.expandAll && (
              <i className="pi pi-angle-down" />
            )}
          </div>
          <div className="sparkline-title w-full">{valueLabel.title ?? "\u00A0"}</div>
        </div>
      )}
      {!props.isMultipleCharts && (
        <div
          id={`${id}`}
          className="ea-sparkline-value"
          style={style}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {!isExpired && mappedValue ? mappedValue : valueLabel.content}
        </div>
      )}

      <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
        {props.lastValueTime && (
          <div>
            <b>{T("word.spark-line.last-value-time")}:</b> {formatTime(props.lastValueTime)} (
            {`${T("word.age")}: `}
            <TimeDiff time={props.lastValueTime} />)
          </div>
        )}
        <div>
          <b>{`${T("word.min")} ${T("word.value").toLowerCase()}`}:</b> {minValue} {props.unit}
        </div>
        <div>
          <b>{`${T("word.max")} ${T("word.value").toLowerCase()}`}:</b> {maxValue} {props.unit}
        </div>
      </Tooltip>
    </>
  );
};

export default SparkLineLabel;
